/**
 * Índice de Utilitários
 * Exporta todos os utilitários do SDK
 */

export { ConfigValidator } from './ConfigValidator.js';
export { Logger } from './Logger.js';

/**
 * Utilitários de Hash
 */
export class HashUtils {
    /**
     * Gera hash SHA-256 usando SubtleCrypto
     */
    static async sha256(data) {
        try {
            if (window.crypto && window.crypto.subtle) {
                const encoder = new TextEncoder();
                const dataBuffer = encoder.encode(data);
                const hashBuffer = await window.crypto.subtle.digest('SHA-256', dataBuffer);
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
            }
            
            // Fallback para hash simples
            return HashUtils.simpleHash(data);
            
        } catch (error) {
            return HashUtils.simpleHash(data);
        }
    }
    
    /**
     * Hash simples para fallback
     */
    static simpleHash(str) {
        let hash = 0;
        if (str.length === 0) return hash.toString();
        
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Converte para 32bit
        }
        
        return Math.abs(hash).toString(16);
    }
    
    /**
     * Gera hash MD5 simples (não criptograficamente seguro)
     */
    static md5(str) {
        // Implementação simples de MD5 para compatibilidade
        function md5cycle(x, k) {
            let a = x[0], b = x[1], c = x[2], d = x[3];
            
            a = ff(a, b, c, d, k[0], 7, -680876936);
            d = ff(d, a, b, c, k[1], 12, -389564586);
            c = ff(c, d, a, b, k[2], 17, 606105819);
            b = ff(b, c, d, a, k[3], 22, -1044525330);
            
            x[0] = add32(a, x[0]);
            x[1] = add32(b, x[1]);
            x[2] = add32(c, x[2]);
            x[3] = add32(d, x[3]);
        }
        
        function cmn(q, a, b, x, s, t) {
            a = add32(add32(a, q), add32(x, t));
            return add32((a << s) | (a >>> (32 - s)), b);
        }
        
        function ff(a, b, c, d, x, s, t) {
            return cmn((b & c) | ((~b) & d), a, b, x, s, t);
        }
        
        function add32(a, b) {
            return (a + b) & 0xFFFFFFFF;
        }
        
        // Implementação simplificada - retorna hash simples
        return HashUtils.simpleHash(str);
    }
    
    /**
     * Gera hash consistente para objetos
     */
    static objectHash(obj) {
        const serialized = HashUtils.serializeObject(obj);
        return HashUtils.simpleHash(serialized);
    }
    
    /**
     * Serializa objeto de forma consistente
     */
    static serializeObject(obj) {
        if (obj === null) return 'null';
        if (obj === undefined) return 'undefined';
        if (typeof obj === 'string') return `"${obj}"`;
        if (typeof obj === 'number') return obj.toString();
        if (typeof obj === 'boolean') return obj.toString();
        
        if (Array.isArray(obj)) {
            return '[' + obj.map(item => HashUtils.serializeObject(item)).join(',') + ']';
        }
        
        if (typeof obj === 'object') {
            const keys = Object.keys(obj).sort();
            const pairs = keys.map(key => `"${key}":${HashUtils.serializeObject(obj[key])}`);
            return '{' + pairs.join(',') + '}';
        }
        
        return obj.toString();
    }
}

/**
 * Utilitários de Performance
 */
export class PerformanceUtils {
    /**
     * Mede tempo de execução de uma função
     */
    static async measureTime(fn, label = 'operation') {
        const startTime = performance.now();
        
        try {
            const result = await fn();
            const endTime = performance.now();
            const duration = endTime - startTime;
            
            return {
                result,
                duration: Math.round(duration * 100) / 100,
                label,
                success: true
            };
        } catch (error) {
            const endTime = performance.now();
            const duration = endTime - startTime;
            
            return {
                error,
                duration: Math.round(duration * 100) / 100,
                label,
                success: false
            };
        }
    }
    
    /**
     * Cria um profiler simples
     */
    static createProfiler(name) {
        const marks = new Map();
        
        return {
            mark(label) {
                marks.set(label, performance.now());
            },
            
            measure(startLabel, endLabel = null) {
                const startTime = marks.get(startLabel);
                const endTime = endLabel ? marks.get(endLabel) : performance.now();
                
                if (startTime === undefined) {
                    throw new Error(`Mark '${startLabel}' não encontrada`);
                }
                
                if (endLabel && endTime === undefined) {
                    throw new Error(`Mark '${endLabel}' não encontrada`);
                }
                
                return Math.round((endTime - startTime) * 100) / 100;
            },
            
            getMarks() {
                return Object.fromEntries(marks);
            },
            
            clear() {
                marks.clear();
            }
        };
    }
    
    /**
     * Throttle function
     */
    static throttle(func, limit) {
        let inThrottle;
        return function() {
            const args = arguments;
            const context = this;
            if (!inThrottle) {
                func.apply(context, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    }
    
    /**
     * Debounce function
     */
    static debounce(func, wait, immediate = false) {
        let timeout;
        return function() {
            const context = this;
            const args = arguments;
            const later = function() {
                timeout = null;
                if (!immediate) func.apply(context, args);
            };
            const callNow = immediate && !timeout;
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
            if (callNow) func.apply(context, args);
        };
    }
    
    /**
     * Mede uso de memória (se disponível)
     */
    static getMemoryUsage() {
        if (performance.memory) {
            return {
                usedJSHeapSize: performance.memory.usedJSHeapSize,
                totalJSHeapSize: performance.memory.totalJSHeapSize,
                jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
                usagePercentage: Math.round(
                    (performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit) * 100
                )
            };
        }
        
        return null;
    }
}

/**
 * Utilitários de Detecção de Ambiente
 */
export class EnvironmentUtils {
    /**
     * Detecta se está em modo de desenvolvimento
     */
    static isDevelopment() {
        return window.location.hostname === 'localhost' || 
               window.location.hostname === '127.0.0.1' ||
               window.location.hostname.includes('dev') ||
               window.location.protocol === 'file:';
    }
    
    /**
     * Detecta tipo de dispositivo
     */
    static getDeviceType() {
        const userAgent = navigator.userAgent.toLowerCase();
        
        if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
            return 'tablet';
        }
        
        if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(userAgent)) {
            return 'mobile';
        }
        
        return 'desktop';
    }
    
    /**
     * Detecta navegador
     */
    static getBrowser() {
        const userAgent = navigator.userAgent;
        
        if (userAgent.includes('Firefox')) {
            return 'Firefox';
        } else if (userAgent.includes('Chrome')) {
            return 'Chrome';
        } else if (userAgent.includes('Safari')) {
            return 'Safari';
        } else if (userAgent.includes('Edge')) {
            return 'Edge';
        } else if (userAgent.includes('Opera')) {
            return 'Opera';
        }
        
        return 'Unknown';
    }
    
    /**
     * Detecta sistema operacional
     */
    static getOS() {
        const userAgent = navigator.userAgent;
        
        if (userAgent.includes('Windows')) {
            return 'Windows';
        } else if (userAgent.includes('Mac')) {
            return 'macOS';
        } else if (userAgent.includes('Linux')) {
            return 'Linux';
        } else if (userAgent.includes('Android')) {
            return 'Android';
        } else if (userAgent.includes('iOS')) {
            return 'iOS';
        }
        
        return 'Unknown';
    }
    
    /**
     * Verifica se está em iframe
     */
    static isInIframe() {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    }
    
    /**
     * Verifica se tem suporte a WebGL
     */
    static hasWebGLSupport() {
        try {
            const canvas = document.createElement('canvas');
            return !!(window.WebGLRenderingContext && 
                     (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
        } catch (e) {
            return false;
        }
    }
    
    /**
     * Verifica se tem suporte a Web Audio
     */
    static hasWebAudioSupport() {
        return !!(window.AudioContext || window.webkitAudioContext);
    }
    
    /**
     * Verifica se tem suporte a Canvas
     */
    static hasCanvasSupport() {
        try {
            const canvas = document.createElement('canvas');
            return !!(canvas.getContext && canvas.getContext('2d'));
        } catch (e) {
            return false;
        }
    }
}

/**
 * Utilitários de Validação
 */
export class ValidationUtils {
    /**
     * Valida URL
     */
    static isValidUrl(string) {
        try {
            new URL(string);
            return true;
        } catch (_) {
            return false;
        }
    }
    
    /**
     * Valida email
     */
    static isValidEmail(email) {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    }
    
    /**
     * Valida se é um número
     */
    static isNumber(value) {
        return typeof value === 'number' && !isNaN(value) && isFinite(value);
    }
    
    /**
     * Valida se é um inteiro positivo
     */
    static isPositiveInteger(value) {
        return ValidationUtils.isNumber(value) && value > 0 && Number.isInteger(value);
    }
    
    /**
     * Valida se é uma string não vazia
     */
    static isNonEmptyString(value) {
        return typeof value === 'string' && value.trim().length > 0;
    }
    
    /**
     * Valida se é um objeto
     */
    static isObject(value) {
        return value !== null && typeof value === 'object' && !Array.isArray(value);
    }
    
    /**
     * Valida se é um array
     */
    static isArray(value) {
        return Array.isArray(value);
    }
    
    /**
     * Valida se é uma função
     */
    static isFunction(value) {
        return typeof value === 'function';
    }
    
    /**
     * Valida range de valores
     */
    static isInRange(value, min, max) {
        return ValidationUtils.isNumber(value) && value >= min && value <= max;
    }
}

/**
 * Utilitários de String
 */
export class StringUtils {
    /**
     * Capitaliza primeira letra
     */
    static capitalize(str) {
        if (!str) return str;
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }
    
    /**
     * Converte para camelCase
     */
    static toCamelCase(str) {
        return str.replace(/[-_\s]+(.)?/g, (_, c) => c ? c.toUpperCase() : '');
    }
    
    /**
     * Converte para kebab-case
     */
    static toKebabCase(str) {
        return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
    }
    
    /**
     * Trunca string
     */
    static truncate(str, length, suffix = '...') {
        if (!str || str.length <= length) return str;
        return str.substring(0, length) + suffix;
    }
    
    /**
     * Remove caracteres especiais
     */
    static sanitize(str) {
        return str.replace(/[^a-zA-Z0-9\s-_]/g, '');
    }
    
    /**
     * Gera string aleatória
     */
    static random(length = 10, charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789') {
        let result = '';
        for (let i = 0; i < length; i++) {
            result += charset.charAt(Math.floor(Math.random() * charset.length));
        }
        return result;
    }
}

/**
 * Utilitários de Array
 */
export class ArrayUtils {
    /**
     * Remove duplicatas
     */
    static unique(arr) {
        return [...new Set(arr)];
    }
    
    /**
     * Agrupa por propriedade
     */
    static groupBy(arr, key) {
        return arr.reduce((groups, item) => {
            const group = item[key];
            groups[group] = groups[group] || [];
            groups[group].push(item);
            return groups;
        }, {});
    }
    
    /**
     * Embaralha array
     */
    static shuffle(arr) {
        const shuffled = [...arr];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }
    
    /**
     * Divide array em chunks
     */
    static chunk(arr, size) {
        const chunks = [];
        for (let i = 0; i < arr.length; i += size) {
            chunks.push(arr.slice(i, i + size));
        }
        return chunks;
    }
    
    /**
     * Calcula média
     */
    static average(arr) {
        if (arr.length === 0) return 0;
        return arr.reduce((sum, num) => sum + num, 0) / arr.length;
    }
    
    /**
     * Encontra mediana
     */
    static median(arr) {
        if (arr.length === 0) return 0;
        const sorted = [...arr].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
    }
}

/**
 * Utilitários de Data
 */
export class DateUtils {
    /**
     * Formata timestamp
     */
    static formatTimestamp(timestamp, format = 'ISO') {
        const date = new Date(timestamp);
        
        switch (format) {
            case 'ISO':
                return date.toISOString();
            case 'locale':
                return date.toLocaleString();
            case 'date':
                return date.toLocaleDateString();
            case 'time':
                return date.toLocaleTimeString();
            default:
                return date.toString();
        }
    }
    
    /**
     * Calcula diferença em milissegundos
     */
    static diff(date1, date2) {
        return Math.abs(new Date(date1) - new Date(date2));
    }
    
    /**
     * Verifica se é hoje
     */
    static isToday(date) {
        const today = new Date();
        const checkDate = new Date(date);
        return checkDate.toDateString() === today.toDateString();
    }
    
    /**
     * Adiciona tempo
     */
    static addTime(date, amount, unit = 'milliseconds') {
        const result = new Date(date);
        
        switch (unit) {
            case 'seconds':
                result.setSeconds(result.getSeconds() + amount);
                break;
            case 'minutes':
                result.setMinutes(result.getMinutes() + amount);
                break;
            case 'hours':
                result.setHours(result.getHours() + amount);
                break;
            case 'days':
                result.setDate(result.getDate() + amount);
                break;
            default:
                result.setTime(result.getTime() + amount);
        }
        
        return result;
    }
}

/**
 * Constantes de utilitários
 */
export const UTILS_CONSTANTS = {
    HASH_ALGORITHMS: ['sha256', 'md5', 'simple'],
    DEVICE_TYPES: ['desktop', 'mobile', 'tablet'],
    BROWSERS: ['Chrome', 'Firefox', 'Safari', 'Edge', 'Opera'],
    OPERATING_SYSTEMS: ['Windows', 'macOS', 'Linux', 'Android', 'iOS'],
    LOG_LEVELS: ['error', 'warn', 'info', 'debug'],
    PERFORMANCE_MARKS: {
        SDK_INIT: 'sdk-init',
        FINGERPRINT_START: 'fingerprint-start',
        FINGERPRINT_END: 'fingerprint-end',
        SEND_START: 'send-start',
        SEND_END: 'send-end'
    }
};